import {
  createContext,
  useEffect,
  useMemo,
  useState,
  Suspense,
  lazy,
} from "react";
import { SocketProvider } from "./AppContext/socketContext";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { themeLight, themeDark, brandColors } from "./theme";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import NavBarWDrawer from "./Components/Navigation/NavBarWDrawer";
import { useSelector } from "react-redux";
import { useLocalStorage } from "./Hooks/useStorage";
import { LicenseInfo } from "@mui/x-license-pro";
import { QueryClient, QueryClientProvider } from "react-query";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import {
  Collapse,
  Fab,
  Paper,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/system";

// Lazy loaded route components
const PublicRoutes = lazy(() => import("./Router/PublicRoutes"));
const AdminRoutes = lazy(() => import("./Router/AdminRoutes"));
const OrgRoutes = lazy(() => import("./Router/OrgRoutes"));
const ParentRoutes = lazy(() => import("./Router/ParentRoutes"));
const StudentRoutes = lazy(() => import("./Router/StudentRoutes"));
const TeacherRoutes = lazy(() => import("./Router/TeacherRoutes"));
const OrgUserRoutes = lazy(() => import("./Router/OrgUserRoutes"));

// Lazy loaded public pages
const Homepage = lazy(() => import("./Screens/Public/Homepage"));
const AboutUs = lazy(() => import("./Screens/Public/Components/AboutUs"));
const Faqs = lazy(() => import("./Screens/Public/Components/Faqs"));
const Rules = lazy(() => import("./Screens/Public/Components/Rules"));
const Guides = lazy(() => import("./Screens/Public/Components/Guides"));
const PrivacyPolicy = lazy(
  () => import("./Screens/Public/Components/PrivacyPolicy"),
);
const TermsAndConditions = lazy(
  () => import("./Screens/Public/Components/TermsAndConditions"),
);

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

export const NotificationContext = createContext(null);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY);

function App() {
  const [themeStorage, setStorage] = useLocalStorage("ChalkItUpTheme", true);

  const [notifications, setNotifications] = useState([]);

  const [theme, setTheme] = useState(themeStorage);
  const [open, setOpen] = useState(false);

  const [accent, setAccent] = useState(() => {
    try { return window.localStorage.getItem("chalkitup.sidebarAccent") || brandColors.indigo; }
    catch { return brandColors.indigo; }
  });

  useEffect(() => {
    const handler = (e) => setAccent(e.detail);
    window.addEventListener("chalkitup:accent", handler);
    return () => window.removeEventListener("chalkitup:accent", handler);
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(`
        _       _ _      _ _
    ___| |_ ___| | |_   |_| |_    _ _ ___
   |  _|   | .'| | '_|  | |  _|  | | | . |
   |___|_|_|__,|_|_,_|  |_|_|    |___|  _|
                                     |_|
`);
      console.log("Looks like we are in development mode!");
    }
  }, []);

  useEffect(() => {
    setStorage(theme);
  }, [theme, setStorage]);

  const muiTheme = useMemo(
    () => createTheme(theme ? themeLight : themeDark, { palette: { primary: { main: accent } } }),
    [theme, accent],
  );

  const role = useSelector((state) => state.userInfo?.user?.role);
  const notificationContextValue = useMemo(
    () => ({
      notifications,
      setNotifications,
    }),
    [notifications],
  );

  // return routes based on user role
  const routes = () => {
    switch (role) {
      case "administrator":
        return <AdminRoutes />;
      case "org-admin":
        return <OrgRoutes />;
      case "org-user":
        return <OrgUserRoutes />;
      case "school-admin":
        return <OrgRoutes />;
      case "school-user":
        return <OrgRoutes />;
      case "parent":
        return <ParentRoutes currentTheme={theme} setTheme={setTheme} />;
      case "student":
        return <StudentRoutes currentTheme={theme} setTheme={setTheme} />;
      case "teacher":
        return <TeacherRoutes />;
      default:
        return <PublicRoutes />;
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <QueryClientProvider client={queryClient}>
        <GoogleReCaptchaProvider
          reCaptchaKey={process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY}
        >
          <NotificationContext.Provider value={notificationContextValue}>
            <SocketProvider>
            <CssBaseline />
            <Router>
              <Suspense fallback={<LoadingFallback />}>
                <Switch>
                  <Route
                    path="/"
                    exact
                    component={Homepage}
                    currentTheme={theme}
                  />
                  <Route
                    path="/AboutUs"
                    exact
                    component={AboutUs}
                    currentTheme={theme}
                  />
                  <Route
                    path="/Faqs"
                    exact
                    component={Faqs}
                    currentTheme={theme}
                  />
                  <Route
                    path="/Rules"
                    exact
                    component={Rules}
                    currentTheme={theme}
                  />
                  <Route
                    path="/Guides"
                    exact
                    component={Guides}
                    currentTheme={theme}
                  />
                  <Route
                    path="/PrivacyPolicy"
                    exact
                    component={PrivacyPolicy}
                    currentTheme={theme}
                  />
                  <Route
                    path="/TermsAndConditions"
                    exact
                    component={TermsAndConditions}
                    currentTheme={theme}
                  />
                  <NavBarWDrawer currentTheme={theme} setTheme={setTheme}>
                    {routes()}
                  </NavBarWDrawer>
                </Switch>
              </Suspense>
              <Box
                sx={{
                  position: "fixed",
                  bottom: 24,
                  right: 24,
                  zIndex: 1300,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1.5,
                }}
              >
                {/* Expanded contact panel — slides in above the FAB */}
                <Collapse in={open} timeout={250}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      width: 288,
                      mb: 0.5,
                      border: (t) => `1px solid ${t.palette.divider}`,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(21,24,33,0.96)"
                          : "rgba(255,255,255,0.98)",
                      backdropFilter: "blur(12px)",
                      boxShadow: (t) =>
                        t.palette.mode === "dark"
                          ? `0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)`
                          : `0 8px 32px rgba(0,0,0,0.1)`,
                    }}
                  >
                    {/* Header */}
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                      <Box sx={{
                        width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
                        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <SupportAgentIcon sx={{ fontSize: 18, color: "#fff" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}>
                          Need help?
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "text.disabled", lineHeight: 1 }}>
                          ChalkItUp Support
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.75, lineHeight: 1.6 }}>
                      For general enquiries, platform support, or any related assistance, reach us at:
                    </Typography>

                    <Box
                      component="a"
                      href="mailto:hello@chalkitup.co.za"
                      sx={{
                        display: "block",
                        px: 1.5, py: 1,
                        borderRadius: 1.5,
                        textDecoration: "none",
                        bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.15 : 0.08),
                        border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        transition: "background .15s ease",
                        "&:hover": {
                          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.24 : 0.14),
                        },
                      }}
                    >
                      hello@chalkitup.co.za
                    </Box>
                  </Paper>
                </Collapse>

                {/* FAB */}
                <Tooltip title={open ? "" : "Need help?"} placement="left">
                  <Fab
                    aria-label="contact us"
                    size="medium"
                    onClick={open ? handleClose : handleClickOpen}
                    sx={{
                      background: open
                        ? (t) => t.palette.mode === "dark" ? t.palette.background.paper : "#fff"
                        : (t) => `linear-gradient(135deg, ${t.palette.primary.light} 0%, ${t.palette.primary.dark} 100%)`,
                      color: (t) => open ? t.palette.text.secondary : "#fff",
                      border: (t) => open ? `1px solid ${t.palette.divider}` : "none",
                      boxShadow: (t) => open
                        ? "0 2px 8px rgba(0,0,0,0.15)"
                        : `0 4px 20px ${alpha(t.palette.primary.main, 0.4)}`,
                      "&:hover": {
                        background: open
                          ? (t) => t.palette.mode === "dark" ? t.palette.action.hover : "#f5f5f7"
                          : (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                        boxShadow: (t) => open
                          ? "0 2px 10px rgba(0,0,0,0.2)"
                          : `0 6px 26px ${alpha(t.palette.primary.main, 0.55)}`,
                        transform: "scale(1.06)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {open ? <CloseIcon /> : <SupportAgentIcon />}
                  </Fab>
                </Tooltip>
              </Box>
            </Router>
            </SocketProvider>
          </NotificationContext.Provider>
        </GoogleReCaptchaProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
